import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { 
  CheckCircle2, 
  Clock, 
  ArrowLeft,
  MapPin,
  Building2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { projectService } from '@/services/projectService';

const statusColors = {
  active: 'bg-success/10 text-success border-success/20',
  completed: 'bg-muted text-muted-foreground border-border',
  planning: 'bg-warning/10 text-warning border-warning/20',
};

const statusLabels = {
  active: 'פעיל',
  completed: 'הושלם',
  planning: 'בתכנון',
};

export default function Dashboard() {
  let { projects, getTodayTasks, getTodayEvents,setProjectsList } = useApp();
  const todayTasks = getTodayTasks();
  const openTasks = todayTasks.filter(t => !t.isCompleted);
  const activeProjects = projects.filter(p => p.status === 'active');

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'בוקר טוב';
    if (hour < 17) return 'צהריים טובים';
    return 'ערב טוב';
  };

  useEffect(() => {
    projectService.getProjects().then((data) => {
      setProjectsList(data);
    }).catch((error) => {
      // Session lifecycle (401 → refresh/logout) is owned by the global Axios interceptor.
      console.error('Error fetching projects:', error);
    })
  }, []);

  return (
    <div className="p-4 md:p-8 space-y-6 animate-fade-in">
      {/* Header */}
      <header className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-bold">{greeting()} 👋</h1>
        <p className="text-muted-foreground">
          {format(new Date(), 'EEEE, d בMMMM yyyy', { locale: he })}
        </p>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card rounded-2xl p-5 border border-border shadow-soft">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary" />
            </div>
          </div>
          <p className="text-3xl font-bold">{openTasks.length}</p>
          <p className="text-sm text-muted-foreground">משימות להיום</p>
        </div>

        <div className="bg-card rounded-2xl p-5 border border-border shadow-soft">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-success" />
            </div>
          </div>
          <p className="text-3xl font-bold">{activeProjects.length}</p>
          <p className="text-sm text-muted-foreground">פרויקטים פעילים</p>
        </div>
      </div>

      {/* Open Tasks Preview */}
      {openTasks.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">משימות להיום</h2>
            <Link to="/calendar" className="text-primary text-sm font-medium flex items-center gap-1">
              הצג הכל
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-2">
            {openTasks.slice(0, 3).map((task) => {
              const project = projects.find(p => p.id === task.projectId);
              return (
                <div
                  key={task.id}
                  className="bg-card rounded-xl p-4 border border-border flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full border-2 border-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{task.title}</p>
                    <p className="text-sm text-muted-foreground truncate">{project?.name}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Active Projects */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">פרויקטים פעילים</h2>
          <Link to="/projects" className="text-primary text-sm font-medium flex items-center gap-1">
            הצג הכל
            <ArrowLeft className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid gap-4">
          {activeProjects.map((project, index) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="bg-card rounded-2xl p-5 border border-border shadow-soft hover:border-primary/50 transition-all group"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg mb-1 truncate group-hover:text-primary transition-colors">
                    {project.name}
                  </h3>
                  <p className="text-muted-foreground text-sm">{project.clientName}</p>
                </div>
                <span className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium border",
                  statusColors[project.status]
                )}>
                  {statusLabels[project.status]}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{project.address}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex gap-2">
                  {project.tags.map((tag) => (
                    <span key={tag} className="px-2 py-1 bg-secondary rounded-md text-secondary-foreground text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
                <span className="text-muted-foreground text-xs">
                  עדכון אחרון: {project.lastUpdate ? format(new Date(project.lastUpdate), 'd/M') : 'לא מעודכן'}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
